import { defineComponent, ref, computed, toRef, onMounted, onUnmounted, createElementBlock, openBlock, createCommentVNode, createElementVNode, toDisplayString, withModifiers, withDirectives, withKeys, vModelText, createVNode, Fragment, renderList, createBlock, resolveDynamicComponent, mergeProps, withCtx, nextTick } from 'vue';
import _sfc_main$2 from './basler-components.es133.js';
import _sfc_main$1 from './basler-components.es81.js';
/* empty css                       */

const _hoisted_1 = { class: "search-box-v2" };
const _hoisted_2 = {
  key: 0,
  class: "search-box-v2__intro-text"
};
const _hoisted_3 = ["aria-expanded", "aria-activedescendant", "placeholder", "onKeydown"];
const _hoisted_4 = {
  type: "submit",
  class: "search-box-v2__btn-submit-search"
};
const _hoisted_5 = { class: "visually-hidden" };
const _hoisted_6 = { class: "visually-hidden" };
const _hoisted_7 = ["innerHTML"];
const _hoisted_8 = ["aria-label", "onKeydown"];
const _hoisted_9 = ["aria-label"];
const _hoisted_10 = {
  role: "presentation",
  class: "search-box-v2__group-label"
};
const _hoisted_11 = ["id", "aria-selected"];
const _hoisted_12 = { class: "search-box-v2__suggestion-item-text" };
const _hoisted_13 = {
  key: 2,
  class: "search-box-v2__badge"
};
const _hoisted_14 = ["onClick"];
const _hoisted_15 = { class: "visually-hidden" };
const inputId = "search-box-input-field-01";
const listId = "search-box-suggestion-list-01";
const suggestionItemPrefix = "search-box-suggestion-item";
const _sfc_main = /* @__PURE__ */ defineComponent({
  __name: "SearchBox",
  props: {
    introText: {},
    inputLabelText: {},
    searchQuery: {},
    inputPlaceholderText: {},
    submitSearchBtnText: {},
    clearSearchBtnText: {},
    clearSuggestionItemBtnText: {},
    suggestionsListTitle: {},
    suggestionGroups: {},
    showShortcutIndicator: { type: Boolean, default: true }
  },
  emits: ["submit", "deleteSuggestion", "update:searchQuery"],
  setup(__props, { expose: __expose, emit: __emit }) {
    const props = __props;
    const emit = __emit;
    const searchInputGroupRef = ref(null);
    const inputRef = ref(null);
    const searchQuery = computed({
      get: () => props.searchQuery ?? "",
      set: (value) => emit("update:searchQuery", value)
    });
    const suggestionGroups = toRef(props, "suggestionGroups");
    const suggestionActiveIndex = ref(-1);
    const showSuggestions = ref(false);
    const isMacPlatform = ref(false);
    const shortcutText = ref("");
    const getShortcutText = (key) => {
      const controlKeyTranslation = navigator.language === "de" || navigator.language === "de-DE" ? "Strg" : "Ctrl";
      if (isMacPlatform.value) return `<kbd>⌘</kbd><kbd>${key}</kbd>`;
      return `<kbd>${controlKeyTranslation}</kbd>+<kbd>${key}</kbd>`;
    };
    const flattenedSuggestions = computed(() => {
      return suggestionGroups.value?.flatMap(
        (group, groupIndex) => group.items.map((item, itemIndex) => ({ item, groupIndex, itemIndex }))
      ) || [];
    });
    const flattenedSuggestionsTotal = computed(() => flattenedSuggestions.value.length);
    const activeDescendant = computed(
      () => suggestionActiveIndex.value >= 0 ? getSuggestionItemId(suggestionActiveIndex.value) : void 0
    );
    const setSuggestionActiveIndex = (index) => {
      suggestionActiveIndex.value = index;
    };
    const getSuggestionItemId = (index) => `${suggestionItemPrefix}-${index}`;
    const selectSuggestionAtIndex = async (index) => {
      const item = flattenedSuggestions.value[index]?.item;
      searchQuery.value = item.title ?? "";
      await nextTick();
      showSuggestions.value = false;
      suggestionActiveIndex.value = -1;
      if (item?.link) {
        const linkId = `${getSuggestionItemId(index)}-link`;
        const linkElement = document.getElementById(linkId);
        if (linkElement) {
          linkElement.click();
        }
      }
      emit("submit", searchQuery.value);
    };
    const suggestionGlobalIndex = (groupIndex, itemIndex) => {
      if (!suggestionGroups?.value) {
        return 0;
      }
      const precedingItemsCount = suggestionGroups.value.slice(0, groupIndex).reduce((total, group) => total + group.items.length, 0);
      return precedingItemsCount + itemIndex;
    };
    const onInput = () => {
      showSuggestions.value = true;
      suggestionActiveIndex.value = -1;
    };
    const onFocus = () => {
      showSuggestions.value = flattenedSuggestionsTotal.value > 0 && searchQuery.value.length > 0;
    };
    const onArrowDown = () => {
      if (flattenedSuggestionsTotal.value === 0) return;
      if (!showSuggestions.value) {
        showSuggestions.value = true;
        suggestionActiveIndex.value = 0;
        return;
      }
      suggestionActiveIndex.value = (suggestionActiveIndex.value + 1) % flattenedSuggestionsTotal.value;
    };
    const onArrowUp = () => {
      if (flattenedSuggestionsTotal.value === 0) return;
      if (!showSuggestions.value) {
        showSuggestions.value = true;
        suggestionActiveIndex.value = flattenedSuggestionsTotal.value - 1;
        return;
      }
      if (suggestionActiveIndex.value === 0) {
        showSuggestions.value = false;
        suggestionActiveIndex.value = -1;
        return;
      } else if (suggestionActiveIndex.value === -1) {
        suggestionActiveIndex.value = 0;
      }
      suggestionActiveIndex.value = (suggestionActiveIndex.value - 1 + flattenedSuggestionsTotal.value) % flattenedSuggestionsTotal.value;
    };
    const onEnter = (event) => {
      if (suggestionActiveIndex.value >= 0 && suggestionActiveIndex.value < flattenedSuggestionsTotal.value) {
        event.preventDefault();
        selectSuggestionAtIndex(suggestionActiveIndex.value);
      }
    };
    const onEsc = () => {
      suggestionActiveIndex.value = -1;
      showSuggestions.value = false;
    };
    const onItemClick = (index, event) => {
      if (event.isTrusted) {
        selectSuggestionAtIndex(index);
      }
    };
    const onClearSearch = () => {
      searchQuery.value = "";
      showSuggestions.value = false;
      suggestionActiveIndex.value = -1;
    };
    const onSubmit = () => {
      showSuggestions.value = false;
      suggestionActiveIndex.value = -1;
      emit("submit", searchQuery.value);
    };
    const onDeleteSuggestion = (item, event) => {
      event.stopPropagation();
      emit("deleteSuggestion", item);
    };
    const onClickOutside = (event) => {
      if (searchInputGroupRef.value && !searchInputGroupRef.value.contains(event.target)) {
        showSuggestions.value = false;
        suggestionActiveIndex.value = -1;
      }
    };
    const focusInput = () => {
      inputRef.value?.focus();
    };
    const searchDropdownIsOpen = computed(() => {
      return showSuggestions.value && flattenedSuggestionsTotal.value > 0;
    });
    __expose({ focus: focusInput, searchDropdownIsOpen });
    onMounted(() => {
      isMacPlatform.value = navigator?.userAgent?.includes("Mac");
      shortcutText.value = getShortcutText("K");
      document.addEventListener("click", onClickOutside);
    });
    onUnmounted(() => {
      document.removeEventListener("click", onClickOutside);
    });
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("div", _hoisted_1, [
        _ctx.introText ? (openBlock(), createElementBlock("div", _hoisted_2, [
          createElementVNode("p", null, toDisplayString(_ctx.introText), 1)
        ])) : createCommentVNode("", true),
        createElementVNode("form", {
          action: "",
          class: "search-box-v2__form",
          onSubmit: withModifiers(onSubmit, ["prevent"])
        }, [
          createElementVNode("div", {
            class: "search-box-v2__input-group",
            ref_key: "searchInputGroupRef",
            ref: searchInputGroupRef
          }, [
            createElementVNode("label", {
              for: inputId,
              class: "visually-hidden"
            }, toDisplayString(_ctx.inputLabelText), 1),
            withDirectives(createElementVNode("input", {
              ref_key: "inputRef",
              ref: inputRef,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event) => searchQuery.value = $event),
              id: inputId,
              type: "search",
              role: "combobox",
              "aria-autocomplete": "list",
              "aria-controls": listId,
              "aria-expanded": flattenedSuggestions.value.length > 0 && showSuggestions.value,
              "aria-activedescendant": activeDescendant.value,
              class: "search-box-v2__input",
              placeholder: _ctx.inputPlaceholderText ? _ctx.inputPlaceholderText : void 0,
              autocomplete: "off",
              onInput,
              onFocus,
              onKeydown: [
                withKeys(withModifiers(onArrowDown, ["prevent"]), ["down"]),
                withKeys(withModifiers(onArrowUp, ["prevent"]), ["up"]),
                withKeys(onEnter, ["enter"]),
                withKeys(withModifiers(onEsc, ["prevent"]), ["esc"])
              ]
            }, null, 40, _hoisted_3), [
              [vModelText, searchQuery.value]
            ]),
            createElementVNode("button", _hoisted_4, [
              createVNode(_sfc_main$1, { name: "magnifier" }),
              createElementVNode("span", _hoisted_5, toDisplayString(_ctx.submitSearchBtnText), 1)
            ]),
            searchQuery.value.length > 0 ? (openBlock(), createElementBlock("button", {
              key: 0,
              type: "button",
              class: "search-box-v2__btn-clear-search",
              onClick: onClearSearch
            }, [
              createVNode(_sfc_main$1, { name: "close" }),
              createElementVNode("span", _hoisted_6, toDisplayString(_ctx.clearSearchBtnText), 1)
            ])) : createCommentVNode("", true),
            _ctx.showShortcutIndicator && shortcutText.value ? (openBlock(), createElementBlock("span", {
              key: 1,
              class: "search-box-v2__shortcut-indicator",
              innerHTML: shortcutText.value
            }, null, 8, _hoisted_7)) : createCommentVNode("", true)
          ], 512),
          showSuggestions.value && suggestionGroups.value && suggestionGroups.value?.length > 0 ? (openBlock(), createElementBlock("div", {
            key: 0,
            id: listId,
            role: "listbox",
            class: "search-box-v2__suggestions",
            "aria-label": _ctx.suggestionsListTitle,
            onKeydown: withKeys(withModifiers(onEsc, ["prevent"]), ["esc"])
          }, [
            (openBlock(true), createElementBlock(Fragment, null, renderList(suggestionGroups.value, (group, groupIndex) => {
              return openBlock(), createElementBlock(Fragment, {
                key: `group-${groupIndex}`
              }, [
                group.items.length > 0 ? (openBlock(), createElementBlock("ul", {
                  key: 0,
                  role: "group",
                  class: "search-box-v2__suggestion-group",
                  "aria-label": group.label
                }, [
                  createElementVNode("li", _hoisted_10, toDisplayString(group.label), 1),
                  (openBlock(true), createElementBlock(Fragment, null, renderList(group.items, (item, itemIndex) => {
                    return openBlock(), createElementBlock("li", {
                      key: `item-${groupIndex}-${itemIndex}`,
                      role: "option",
                      id: getSuggestionItemId(suggestionGlobalIndex(groupIndex, itemIndex)),
                      "aria-selected": suggestionActiveIndex.value === suggestionGlobalIndex(groupIndex, itemIndex),
                      class: "search-box-v2__suggestion-row"
                    }, [
                      (openBlock(), createBlock(resolveDynamicComponent(item.link ? _sfc_main$2 : "div"), mergeProps({ ref_for: true }, item.link, {
                        id: `${getSuggestionItemId(suggestionGlobalIndex(groupIndex, itemIndex))}-link`,
                        class: "search-box-v2__suggestion-item",
                        onMouseover: ($event) => setSuggestionActiveIndex(suggestionGlobalIndex(groupIndex, itemIndex)),
                        onClick: ($event) => onItemClick(suggestionGlobalIndex(groupIndex, itemIndex), $event)
                      }), {
                        default: withCtx(() => [
                          item.iconBefore ? (openBlock(), createBlock(_sfc_main$1, {
                            key: 0,
                            name: item.iconBefore,
                            class: "search-box-v2__suggestion-item-icon-before"
                          }, null, 8, ["name"])) : createCommentVNode("", true),
                          createElementVNode("span", _hoisted_12, toDisplayString(item.title), 1),
                          item.iconAfter ? (openBlock(), createBlock(_sfc_main$1, {
                            key: 1,
                            name: item.iconAfter,
                            class: "search-box-v2__suggestion-item-icon-after"
                          }, null, 8, ["name"])) : createCommentVNode("", true),
                          item.badge ? (openBlock(), createElementBlock("span", _hoisted_13, toDisplayString(item.badge.text), 1)) : createCommentVNode("", true),
                          item.isDeletable ? (openBlock(), createElementBlock("button", {
                            key: 3,
                            type: "button",
                            class: "search-box-v2__btn-clear-suggestion",
                            onClick: ($event) => onDeleteSuggestion(item, $event)
                          }, [
                            createVNode(_sfc_main$1, {
                              name: "bin",
                              "scale-factor": 1.2
                            }),
                            createElementVNode("span", _hoisted_15, toDisplayString(_ctx.clearSuggestionItemBtnText) + ' "' + toDisplayString(item.title) + '"', 1)
                          ], 8, _hoisted_14)) : createCommentVNode("", true)
                        ]),
                        _: 2
                      }, 1040, ["id", "onMouseover", "onClick"]))
                    ], 8, _hoisted_11);
                  }), 128))
                ], 8, _hoisted_9)) : createCommentVNode("", true)
              ], 64);
            }), 128))
          ], 40, _hoisted_8)) : createCommentVNode("", true)
        ], 32)
      ]);
    };
  }
});

export { _sfc_main as default };
